// menuConfig.js - Menu Configuration with Jewellery Module Integration

import {
  ShoppingCartIcon,
  TagIcon,
  CubeIcon,
  UserGroupIcon,
  PhoneIcon,
  BellIcon,
  ChartBarIcon,
  CogIcon,
  HomeIcon,
  SparklesIcon,
  ClipboardCheckIcon,
  DocumentTextIcon,
  UsersIcon,
  ShieldCheckIcon,
  PlusCircleIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  GiftIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  DocumentReportIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  OfficeBuildingIcon,
  PhotographIcon,
  CollectionIcon,
  LocationMarkerIcon,
  SwitchHorizontalIcon,
  AdjustmentsIcon,
  DeviceMobileIcon,
} from "@heroicons/react/outline";
import { TrendingUpIcon } from "@heroicons/react/solid";
import {
  FiPackage,
  FiTruck,
  FiUpload,
  FiDownload,
  FiAward,
  FiDollarSign,
  FiSearch,
  FiFileText,
  FiAlertTriangle,
  FiSettings,
  FiImage,
  FiTag,
  FiMapPin,
  FiToggleRight,
  FiSliders,
} from "react-icons/fi";

import { PRODUCT_ROUTES, PRODUCT_PERMISSIONS } from "./productRoutes";

import {
  NOTIFICATION_ROUTES,
  NOTIFICATION_PERMISSIONS,
} from "../constants/notification.constant";
import {
  JEWELLERY_ROUTES,
  JEWELLERY_PERMISSIONS,
} from "../constants/jewellery.constant";

import {
  APPOINTMENT_ROUTES,
  APPOINTMENT_PERMISSIONS,
  APPOINTMENTS_MENU_GROUP,
  APPOINTMENTS_QUICK_ACCESS,
} from "../pages/Appointments";

import { CONSULTATION_MENU_GROUP } from "../pages/Consultation/config/consultationMenuConfig";

// Import Karigar Management
import {
  KARIGAR_MENU_GROUP,
  KARIGAR_QUICK_ACCESS,
} from "../pages/KarigarManagement/config/karigarMenuConfig";
import {
  KARIGAR_ROUTES,
  KARIGAR_PERMISSIONS,
} from "../pages/KarigarManagement/constants/karigar.constant";

// App Settings Routes Constants
export const APP_SETTINGS_ROUTES = {
  ROOT: "/app-settings",
  DASHBOARD: "/app-settings/dashboard",
  BANNERS: "/app-settings/banners",
  BANNERS_CREATE: "/app-settings/banners/new",
  COLLECTIONS: "/app-settings/collections",
  COLLECTIONS_CREATE: "/app-settings/collections/new",
  CONTACT: "/app-settings/contact",
  CONTACT_CREATE: "/app-settings/contact/new",
  FEATURE_FLAGS: "/app-settings/feature-flags",
  CONFIG: "/app-settings/config",
  CONFIG_CREATE: "/app-settings/config/new",
};

// App Settings Permissions Constants
export const APP_SETTINGS_PERMISSIONS = {
  VIEW_SETTINGS: "VIEW_APP_SETTINGS",
  MANAGE_BANNERS: "MANAGE_BANNERS",
  MANAGE_COLLECTIONS: "MANAGE_COLLECTIONS",
  MANAGE_CONTACT: "MANAGE_CONTACT_INFO",
  MANAGE_FLAGS: "MANAGE_FEATURE_FLAGS",
  MANAGE_CONFIG: "MANAGE_APP_CONFIG",
};

// Menu groups with color coding for better visual identification
export const MENU_GROUPS = {
  MAIN: "main",
  JEWELLERY: "jewellery",
  KARIGAR: "karigar", // Added Karigar
  NOTIFICATIONS: "notifications",
  COMPANY_SETTINGS: "company_settings",
  APP_SETTINGS: "app_settings",
};

// Color schemes for each group (used for visual identification)
export const GROUP_COLORS = {
  [MENU_GROUPS.MAIN]: {
    bg: "bg-blue-500",
    hover: "hover:bg-blue-600",
    light: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-500",
  },
  [MENU_GROUPS.JEWELLERY]: {
    bg: "bg-yellow-500",
    hover: "hover:bg-yellow-600",
    light: "bg-yellow-50",
    text: "text-yellow-600",
    border: "border-yellow-500",
  },
  [MENU_GROUPS.KARIGAR]: {
    bg: "bg-amber-500",
    hover: "hover:bg-amber-600",
    light: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-500",
  },
  [MENU_GROUPS.NOTIFICATIONS]: {
    bg: "bg-red-500",
    hover: "hover:bg-red-600",
    light: "bg-red-50",
    text: "text-red-600",
    border: "border-red-500",
  },
  [MENU_GROUPS.APP_SETTINGS]: {
    bg: "bg-purple-500",
    hover: "hover:bg-purple-600",
    light: "bg-purple-50",
    text: "text-purple-600",
    border: "border-purple-500",
  },
  [MENU_GROUPS.COMPANY_SETTINGS]: {
    bg: "bg-gray-500",
    hover: "hover:bg-gray-600",
    light: "bg-gray-50",
    text: "text-gray-600",
    border: "border-gray-500",
  },
};

// Default feature configuration
export const DEFAULT_FEATURES_CONFIG = {
  quickAccess: true,
  searchBar: true,
  groupHeaders: true,
  menuCollapsing: true,
  languageToggle: true,
  groupBadges: true,
  tooltips: true,
  animations: true,
  jewelleryPlans: true,
  karigarManagement: true, // Added Karigar feature flag
};

// Menu structure aligned with available routes - using translation keys
export const menuConfig = [
  {
    id: MENU_GROUPS.MAIN,
    translationKey: "menu.groups.main",
    icon: HomeIcon,
    color: GROUP_COLORS[MENU_GROUPS.MAIN],
    expanded: true,
    items: [
      {
        path: "/orders",
        translationKey: "menu.items.orders",
        descriptionKey: "menu.descriptions.orders",
        icon: ShoppingCartIcon,
        permissions: ["VIEW_ORDERS", "ADMIN"],
        featureFlag: "orders",
      },

      {
        path: "/products",
        translationKey: "menu.items.products",
        descriptionKey: "menu.descriptions.products",
        icon: CubeIcon,
        permissions: ["VIEW_PRODUCTS", "ADMIN"],
        featureFlag: "products",
        // Add submenu for product features
        subItems: [
          {
            path: PRODUCT_ROUTES.LIST,
            translationKey: "menu.items.product_list",
            descriptionKey: "menu.descriptions.product_list",
            icon: CubeIcon,
            permissions: [PRODUCT_PERMISSIONS.VIEW_PRODUCTS],
            featureFlag: "productList",
          },
          {
            path: PRODUCT_ROUTES.CREATE,
            translationKey: "menu.items.create_product",
            descriptionKey: "menu.descriptions.create_product",
            icon: PlusCircleIcon,
            permissions: [PRODUCT_PERMISSIONS.CREATE_PRODUCTS],
            featureFlag: "createProduct",
          },
          {
            path: PRODUCT_ROUTES.STOCK_TRANSFER,
            translationKey: "menu.items.stock_transfer",
            descriptionKey: "menu.descriptions.stock_transfer",
            icon: FiTruck,
            permissions: [PRODUCT_PERMISSIONS.TRANSFER_STOCK],
            featureFlag: "stockTransfer",
          },
          {
            path: PRODUCT_ROUTES.LOW_STOCK_REPORT,
            translationKey: "menu.items.low_stock",
            descriptionKey: "menu.descriptions.low_stock",
            icon: FiAlertTriangle,
            permissions: [PRODUCT_PERMISSIONS.VIEW_REPORTS],
            featureFlag: "lowStock",
            badge: { type: "warning", count: 0 }, // Dynamic count
          },
          {
            path: PRODUCT_ROUTES.BULK_OPERATIONS,
            translationKey: "menu.items.bulk_operations",
            descriptionKey: "menu.descriptions.bulk_operations",
            icon: FiPackage,
            permissions: [PRODUCT_PERMISSIONS.BULK_OPERATIONS],
            featureFlag: "bulkOperations",
          },
          {
            path: PRODUCT_ROUTES.IMPORT,
            translationKey: "menu.items.import_products",
            descriptionKey: "menu.descriptions.import_products",
            icon: FiUpload,
            permissions: [PRODUCT_PERMISSIONS.IMPORT_EXPORT],
            featureFlag: "importProducts",
          },
          {
            path: PRODUCT_ROUTES.EXPORT,
            translationKey: "menu.items.export_products",
            descriptionKey: "menu.descriptions.export_products",
            icon: FiDownload,
            permissions: [PRODUCT_PERMISSIONS.IMPORT_EXPORT],
            featureFlag: "exportProducts",
          },
          {
            path: PRODUCT_ROUTES.HALLMARK_VERIFICATION,
            translationKey: "menu.items.hallmark_verify",
            descriptionKey: "menu.descriptions.hallmark_verify",
            icon: FiAward,
            permissions: [PRODUCT_PERMISSIONS.MANAGE_HALLMARK],
            featureFlag: "hallmarkVerify",
          },
          {
            path: PRODUCT_ROUTES.BULK_PRICING,
            translationKey: "menu.items.bulk_pricing",
            descriptionKey: "menu.descriptions.bulk_pricing",
            icon: FiDollarSign,
            permissions: [PRODUCT_PERMISSIONS.MANAGE_PRICING],
            featureFlag: "bulkPricing",
          },
          {
            path: PRODUCT_ROUTES.PRODUCT_REPORTS,
            translationKey: "menu.items.product_reports",
            descriptionKey: "menu.descriptions.product_reports",
            icon: FiFileText,
            permissions: [PRODUCT_PERMISSIONS.VIEW_REPORTS],
            featureFlag: "productReports",
          },
        ],
      },
      {
        path: "/cart",
        translationKey: "menu.items.cart",
        descriptionKey: "menu.descriptions.cart",
        icon: ShoppingCartIcon,
        permissions: ["VIEW_CART", "ADMIN"],
        featureFlag: "cart",
      },
      {
        path: "/category",
        translationKey: "menu.items.categories",
        descriptionKey: "menu.descriptions.categories",
        icon: TagIcon,
        permissions: ["VIEW_CATEGORIES", "ADMIN"],
        featureFlag: "categories",
      },
      {
        path: "/customers",
        translationKey: "menu.items.customers",
        descriptionKey: "menu.descriptions.customers",
        icon: UserGroupIcon,
        permissions: ["VIEW_USERS", "ADMIN"],
        featureFlag: "customers",
      },
      {
        path: "/customer-requests",
        translationKey: "menu.items.customer_requests",
        descriptionKey: "menu.descriptions.customer_requests",
        icon: ClipboardCheckIcon,
        permissions: ["VIEW_REQUESTS", "ADMIN"],
        featureFlag: "customerRequests",
      },
    ],
  },
  CONSULTATION_MENU_GROUP,
  APPOINTMENTS_MENU_GROUP,
  KARIGAR_MENU_GROUP, // Added Karigar Menu Group
  {
    id: MENU_GROUPS.JEWELLERY,
    translationKey: "menu.groups.jewellery_plans",
    icon: SparklesIcon,
    color: GROUP_COLORS[MENU_GROUPS.JEWELLERY],
    expanded: true,
    featureFlag: "jewelleryPlans",
    items: [
      {
        path: JEWELLERY_ROUTES.DASHBOARD,
        translationKey: "menu.items.plans_dashboard",
        descriptionKey: "menu.descriptions.plans_dashboard",
        icon: ChartBarIcon,
        permissions: [JEWELLERY_PERMISSIONS.VIEW_PLANS, "ADMIN"],
        featureFlag: "plansDashboard",
        badge: { type: "info", count: 0 },
      },
      {
        path: JEWELLERY_ROUTES.CREATE_PLAN,
        translationKey: "menu.items.create_plan",
        descriptionKey: "menu.descriptions.create_plan",
        icon: PlusCircleIcon,
        permissions: [JEWELLERY_PERMISSIONS.CREATE_PLANS, "ADMIN"],
        featureFlag: "createPlan",
      },
      {
        path: JEWELLERY_ROUTES.ACTIVE_PLANS,
        translationKey: "menu.items.active_plans",
        descriptionKey: "menu.descriptions.active_plans",
        icon: ClockIcon,
        permissions: [JEWELLERY_PERMISSIONS.VIEW_PLANS],
        featureFlag: "activePlans",
      },
      {
        path: JEWELLERY_ROUTES.REDEMPTIONS,
        translationKey: "menu.items.redemptions",
        descriptionKey: "menu.descriptions.redemptions",
        icon: GiftIcon,
        permissions: [JEWELLERY_PERMISSIONS.VIEW_REDEMPTIONS],
        featureFlag: "redemptions",
      },
      {
        path: JEWELLERY_ROUTES.TEMPLATES,
        translationKey: "menu.items.plan_templates",
        descriptionKey: "menu.descriptions.plan_templates",
        icon: DocumentDuplicateIcon,
        permissions: [JEWELLERY_PERMISSIONS.MANAGE_TEMPLATES, "ADMIN"],
        featureFlag: "planTemplates",
      },
    ],
  },

  {
    id: MENU_GROUPS.NOTIFICATIONS,
    translationKey: "menu.groups.notifications",
    icon: BellIcon,
    color: GROUP_COLORS[MENU_GROUPS.NOTIFICATIONS],
    expanded: false,
    featureFlag: "notifications",
    items: [
      {
        path: NOTIFICATION_ROUTES.ROOT,
        translationKey: "menu.items.notifications_dashboard",
        descriptionKey: "menu.descriptions.notifications_dashboard",
        icon: BellIcon,
        permissions: [
          NOTIFICATION_PERMISSIONS.VIEW,
          NOTIFICATION_PERMISSIONS.ADMIN,
        ],
        featureFlag: "notificationsDashboard",
      },
      {
        path: NOTIFICATION_ROUTES.TEMPLATES,
        translationKey: "menu.items.templates",
        descriptionKey: "menu.descriptions.templates",
        icon: DocumentTextIcon,
        permissions: [NOTIFICATION_PERMISSIONS.ADMIN],
        featureFlag: "notificationsTemplates",
      },
      {
        path: NOTIFICATION_ROUTES.JOBS,
        translationKey: "menu.items.jobs",
        descriptionKey: "menu.descriptions.jobs",
        icon: ClipboardCheckIcon,
        permissions: [
          NOTIFICATION_PERMISSIONS.VIEW,
          NOTIFICATION_PERMISSIONS.ADMIN,
        ],
        featureFlag: "notificationsJobs",
      },
      {
        path: NOTIFICATION_ROUTES.DEVICES,
        translationKey: "menu.items.devices",
        descriptionKey: "menu.descriptions.devices",
        icon: PhoneIcon,
        permissions: [NOTIFICATION_PERMISSIONS.ADMIN],
        featureFlag: "notificationsDevices",
      },
    ],
  },

  {
    id: "rates",
    translationKey: "menu.groups.rates",
    icon: CurrencyDollarIcon,
    color: {
      bg: "bg-amber-500",
      hover: "hover:bg-amber-600",
      light: "bg-amber-50",
      text: "text-amber-600",
      border: "border-amber-500",
    },
    expanded: false,
    featureFlag: "rateManagement",
    items: [
      {
        path: "/rates",
        translationKey: "menu.items.rates_dashboard",
        descriptionKey: "menu.descriptions.rates_dashboard",
        icon: ChartBarIcon,
        permissions: ["VIEW_RATES", "ADMIN"],
        featureFlag: "ratesDashboard",
      },
      {
        path: "/rates/daily",
        translationKey: "menu.items.daily_rates",
        descriptionKey: "menu.descriptions.daily_rates",
        icon: CalendarIcon,
        permissions: ["MANAGE_RATES", "ADMIN"],
        featureFlag: "dailyRates",
        badge: { type: "warning", count: 0 },
      },
      {
        path: "/rates/stones",
        translationKey: "menu.items.stone_rates",
        descriptionKey: "menu.descriptions.stone_rates",
        icon: SparklesIcon,
        permissions: ["MANAGE_RATES", "ADMIN"],
        featureFlag: "stoneRates",
      },
      {
        path: "/rates/charges",
        translationKey: "menu.items.rate_charges",
        descriptionKey: "menu.descriptions.rate_charges",
        icon: CogIcon,
        permissions: ["MANAGE_RATES", "ADMIN"],
        featureFlag: "rateCharges",
      },
      {
        path: "/rates/analytics",
        translationKey: "menu.items.rate_analytics",
        descriptionKey: "menu.descriptions.rate_analytics",
        icon: ChartBarIcon,
        permissions: ["VIEW_RATES", "ADMIN"],
        featureFlag: "rateAnalytics",
      },
    ],
  },
  {
    id: MENU_GROUPS.APP_SETTINGS,
    translationKey: "menu.groups.app_settings",
    icon: DeviceMobileIcon,
    color: GROUP_COLORS[MENU_GROUPS.APP_SETTINGS],
    expanded: false,
    featureFlag: "appSettings",
    items: [
      {
        path: APP_SETTINGS_ROUTES.DASHBOARD,
        translationKey: "menu.items.app_settings_dashboard",
        descriptionKey: "menu.descriptions.app_settings_dashboard",
        icon: ChartBarIcon,
        permissions: [APP_SETTINGS_PERMISSIONS.VIEW_SETTINGS, "ADMIN"],
        featureFlag: "appSettingsDashboard",
      },
      {
        path: APP_SETTINGS_ROUTES.BANNERS,
        translationKey: "menu.items.banners",
        descriptionKey: "menu.descriptions.banners",
        icon: FiImage,
        permissions: [APP_SETTINGS_PERMISSIONS.MANAGE_BANNERS, "ADMIN"],
        featureFlag: "manageBanners",
        badge: { type: "info", count: 0 },
      },
      {
        path: APP_SETTINGS_ROUTES.COLLECTIONS,
        translationKey: "menu.items.collections",
        descriptionKey: "menu.descriptions.collections",
        icon: CollectionIcon,
        permissions: [APP_SETTINGS_PERMISSIONS.MANAGE_COLLECTIONS, "ADMIN"],
        featureFlag: "manageCollections",
      },
      // {
      //   path: APP_SETTINGS_ROUTES.CONTACT,
      //   translationKey: "menu.items.contact_info",
      //   descriptionKey: "menu.descriptions.contact_info",
      //   icon: FiMapPin,
      //   permissions: [APP_SETTINGS_PERMISSIONS.MANAGE_CONTACT, "ADMIN"],
      //   featureFlag: "manageContactInfo",
      // },
      // {
      //   path: APP_SETTINGS_ROUTES.FEATURE_FLAGS,
      //   translationKey: "menu.items.feature_flags",
      //   descriptionKey: "menu.descriptions.feature_flags",
      //   icon: FiToggleRight,
      //   permissions: [APP_SETTINGS_PERMISSIONS.MANAGE_FLAGS, "ADMIN"],
      //   featureFlag: "manageFeatureFlags",
      // },
      // {
      //   path: APP_SETTINGS_ROUTES.CONFIG,
      //   translationKey: "menu.items.app_config",
      //   descriptionKey: "menu.descriptions.app_config",
      //   icon: FiSliders,
      //   permissions: [APP_SETTINGS_PERMISSIONS.MANAGE_CONFIG, "ADMIN"],
      //   featureFlag: "manageAppConfig",
      // },
    ],
  },
  {
    id: MENU_GROUPS.COMPANY_SETTINGS,
    translationKey: "menu.groups.company_settings",
    icon: CogIcon,
    color: GROUP_COLORS[MENU_GROUPS.COMPANY_SETTINGS],
    expanded: false,
    featureFlag: "companySettings",
    items: [
      {
        path: "/branches",
        translationKey: "menu.items.branch_list",
        descriptionKey: "menu.descriptions.branch_list",
        icon: OfficeBuildingIcon,
        permissions: ["VIEW_BRANCHES", "ADMIN"],
        featureFlag: "viewBranches",
      },
      {
        path: "/pricing-rules",
        translationKey: "menu.items.pricing_rules",
        descriptionKey: "menu.descriptions.pricing_rules",
        icon: CurrencyDollarIcon,
        permissions: ["MANAGE_PRICING", "ADMIN"],
        featureFlag: "pricingRules",
      },
      {
        path: "/admin",
        translationKey: "menu.items.admins",
        descriptionKey: "menu.descriptions.admins",
        icon: ShieldCheckIcon,
        permissions: ["ADMIN"],
        featureFlag: "adminManagement",
      },
      {
        path: "/usage",
        translationKey: "menu.items.usage_dashboard",
        descriptionKey: "menu.descriptions.usage_dashboard",
        icon: ChartBarIcon,
        permissions: ["VIEW_USAGE", "ADMIN"],
        featureFlag: "usageDashboard",
      },
      {
        path: "/company-details",
        translationKey: "menu.items.company_details",
        descriptionKey: "menu.descriptions.company_details",
        icon: OfficeBuildingIcon,
        permissions: ["ADMIN"],
        featureFlag: "companyDetails",
      },
    ],
  },
];

// Quick access for most used features (always visible)
export const quickAccessItems = [
  ...APPOINTMENTS_QUICK_ACCESS,
  ...KARIGAR_QUICK_ACCESS, // Added Karigar Quick Access
];

// Get menu items filtered by permissions and feature flags
export const getFilteredMenuConfig = (
  userPermissions = [],
  isRootAdmin = false,
  featuresConfig = {},
) => {
  // Merge default features config with provided config
  const features = { ...DEFAULT_FEATURES_CONFIG, ...featuresConfig };

  return menuConfig
    .filter((group) => {
      // If the group has a feature flag, check if it's enabled
      // if (group.featureFlag && features[group.featureFlag] === false) {
      //   return false;
      // }
      return true;
    })
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        return true;
        // Check feature flag if present
        if (item.featureFlag && features[item.featureFlag] === false) {
          return false;
        }

        // Check permissions
        if (!item.permissions || item.permissions.length === 0) {
          return true;
        }

        return item.permissions.some(
          (permission) => userPermissions.includes(permission) || isRootAdmin,
        );
      }),
    }))
    .filter((group) => group.items.length > 0);
};

// Get filtered quick access items
export const getFilteredQuickAccessItems = (
  userPermissions = [],
  isRootAdmin = false,
  featuresConfig = {},
) => {
  // Merge default features config with provided config
  const features = { ...DEFAULT_FEATURES_CONFIG, ...featuresConfig };

  // If quick access is disabled, return empty array
  if (features.quickAccess === false) {
    return [];
  }

  return quickAccessItems.filter((item) => {
    return true;
    // Check feature flag if present
    if (item.featureFlag && features[item.featureFlag] === false) {
      return false;
    }

    // Check permissions
    if (!item.permissions || item.permissions.length === 0) {
      return true;
    }

    return item.permissions.some(
      (permission) => userPermissions.includes(permission) || isRootAdmin,
    );
  });
};

// Function to get flat list of menu items for search and permission checks
export const getFlatMenuItems = (menuItems) => {
  let flatItems = [];

  menuItems.forEach((group) => {
    if (group.items) {
      group.items.forEach((item) => {
        flatItems.push({
          id: item.path,
          title: item.translationKey,
          path: item.path,
          permissions: item.permissions,
          groupId: group.id,
        });
      });
    }
  });

  return flatItems;
};

// Export Karigar routes and permissions for use elsewhere
export { KARIGAR_ROUTES, KARIGAR_PERMISSIONS };

export default menuConfig;
